<template>
  <div class="p-4 mb-8 w-full h-full min-h-fit">
    <div
      class="flex items-center justify-between mb-10 flex-wrap lg:flex-nowrap w-full gap-3 h-full"
    >
      <TextField v-model="globalFilter" class="w-full max-w-64" placeholder="البحث" />

      <div class="w-full">
        <slot name="buttons"></slot>
      </div>
    </div>
    <div class="h-full overflow-x-auto">
      <table class="w-full box-border">
        <thead>
          <!-- Table headers -->
          <tr
            class="border-b border-slate-200 dark:border-gray-700"
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              class="p-3 dark:text-gray-200"
              v-for="header in headerGroup.headers"
              :key="header.id"
              :colSpan="header.colSpan"
              :class="header.column.getCanSort() ? 'cursor-pointer select-none' : ''"
              @click="header.column.getToggleSortingHandler()?.($event)"
            >
              <div class="flex items-center gap-2">
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
                <ArrowDownIcon v-if="header.column.getIsSorted() === 'desc'" class="w-4 h-4" />
                <ArrowUpIcon v-if="header.column.getIsSorted() === 'asc'" class="w-4 h-4" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            @click="(event) => handleRowClick(event, row)"
            class="bg-white dark:bg-gray-800 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
          >
            <td v-for="cell in row.getVisibleCells()" :key="cell.id" class="px-6 py-4 dark:text-gray-200">
              <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="flex items-center gap-8 mt-4 justify-end">
      <button
        class="rounded-md border border-slate-300 dark:border-gray-600 p-2.5 text-center text-sm transition-all shadow-sm hover:shadow-lg text-slate-600 dark:text-gray-300 hover:text-white hover:bg-primary hover:border-slate-800 focus:text-white focus:bg-slate-800 focus:border-slate-800 active:border-slate-800 active:text-white active:bg-slate-800 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
        @click="() => table.previousPage()"
        :disabled="!table.getCanPreviousPage()"
        type="button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="w-4 h-4"
        >
          <path
            fill-rule="evenodd"
            d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z"
            clip-rule="evenodd"
          />
        </svg>
      </button>

      <p class="text-slate-600 dark:text-gray-300" v-if="data.length > 0">
        صفحة
        <strong class="text-slate-800 dark:text-gray-100">{{ table.getState().pagination.pageIndex + 1 }}</strong>
        من&nbsp;<strong class="text-slate-800 dark:text-gray-100">{{ table.getPageCount() }}</strong>
      </p>

      <button
        class="rounded-md border border-slate-300 dark:border-gray-600 p-2.5 text-center text-sm transition-all shadow-sm hover:shadow-lg text-slate-600 dark:text-gray-300 hover:text-white hover:bg-primary hover:border-slate-800 focus:text-white focus:bg-slate-800 focus:border-slate-800 active:border-slate-800 active:text-white active:bg-slate-800 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
        @click="() => table.nextPage()"
        :disabled="!table.getCanNextPage()"
        type="button"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="w-4 h-4"
        >
          <path
            fill-rule="evenodd"
            d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="tsx" generic="T">
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/vue/24/outline'
import {
  type ColumnFiltersState,
  createColumnHelper,
  FlexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  type RowSelectionState,
  type SortingState,
  useVueTable
} from '@tanstack/vue-table'
import { ref } from 'vue'
import TextField from './TextField.vue'

const props = defineProps<{
  data: Array<T>
  headers: Array<any>
  rowSelection?: boolean
}>()

function handleRowClick(event: any, row: Row<T>) {
  emit('rowClick', row)
  row.getToggleSelectedHandler()?.(event)
}

const emit = defineEmits<{
  (e: 'rowClick', row: Row<T>): void
}>()

const sorting = ref<SortingState>([])
const globalFilter = ref('')
const rowSelection = ref<RowSelectionState>({})

const table = useVueTable({
  get data() {
    return props.data ?? []
  },
  get columns() {
    return props.rowSelection
      ? [
          {
            id: 'select',
            header: ({ table }: { table: any }) => {
              return (
                <input
                  class="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  type="checkbox"
                  checked={table.getIsAllRowsSelected()}
                  indeterminate={table.getIsSomeRowsSelected()}
                  onInput={table.getToggleAllRowsSelectedHandler()}
                />
              )
            },
            cell: ({ row }: { row: any }) => {
              return (
                <input
                  class="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  type="checkbox"
                  checked={row.getIsSelected()}
                  disabled={!row.getCanSelect()}
                  onInput={row.getToggleSelectedHandler()}
                />
              )
            }
          },
          ,
          ...props.headers
        ]
      : props.headers
  },
  state: {
    get sorting() {
      return sorting.value
    },
    get globalFilter() {
      return globalFilter.value
    },
    get rowSelection() {
      return rowSelection.value
    }
  },
  enableRowSelection: props.rowSelection, //enable row selection for all rows

  onGlobalFilterChange: (updaterOrValue) => {
    globalFilter.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(globalFilter.value) : updaterOrValue
  },
  onSortingChange: (updaterOrValue) => {
    sorting.value =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting.value) : updaterOrValue
  },
  onRowSelectionChange: (updateOrValue) => {
    rowSelection.value =
      typeof updateOrValue === 'function' ? updateOrValue(rowSelection.value) : updateOrValue
  },

  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getPaginationRowModel: getPaginationRowModel()
})

function getSelectedRows() {
  return table.getSelectedRowModel().rows
}

function clearRowSelection() {
  rowSelection.value = {}
}

defineExpose({
  getSelectedRows,
  clearRowSelection
})
</script>

<style scoped></style>
